import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

import { PERSIST_SEARCH_PARAMS } from '~/modules/artist-roster/hooks/useReleasesUrlParams/constants';
import { areUrlParamsEqual } from '~/modules/artist-roster/hooks/useUrlParams/utils/areUrlParamsEqual';

import { useUrlParams } from '../useUrlParams';
import type { ArtistUrlParamsHook } from '../useUrlParams/types';
import {
  formatFiltersToUrlParams,
  parseFiltersFromUrlParams,
} from './parsers/filters';
import type { ReleasesFilters } from './types';

/**
 * Hook to manage URL parameters for the tracks page.
 * {@link ArtistUrlParamsHook}
 */
export function useReleasesUrlParams(): ArtistUrlParamsHook<ReleasesFilters> {
  const [searchParams, setSearchParams] = useSearchParams();

  const properties = useUrlParams<ReleasesFilters>(
    formatFiltersToUrlParams,
    parseFiltersFromUrlParams,
  );

  // Override the setFilters method from useUrlParams to include persistence of search params.
  const setFilters = useCallback(
    (newFilters: Partial<ReleasesFilters>) => {
      const newFilterParams = formatFiltersToUrlParams(newFilters);
      const existingFilterUrlParams = formatFiltersToUrlParams(
        properties.filters,
      );

      if (areUrlParamsEqual(newFilterParams, existingFilterUrlParams)) {
        return;
      }

      for (const param of PERSIST_SEARCH_PARAMS) {
        const value = searchParams.get(param);

        if (value) {
          newFilterParams.set(param, value);
        }
      }

      setSearchParams(newFilterParams);
    },
    [properties.filters, searchParams, setSearchParams],
  );

  return {
    ...properties,
    setFilters,
  };
}
